import React, { useMemo } from "react";
import styled from "styled-components";
import { ArrowRight } from "lucide-react";
import { CARD_COLOR, renderProductIcon } from "../../constants/productConstants";
import { getEffectiveCancelsAt } from "../../helper";

const MAX_STACK_ICONS = 5;

const PlansSummaryCard = ({ items, subscription, unsubscribedProducts = [], onViewAll }) => {
  const { totalFormatted, cycleLabel, renewalDate, pendingCancelCount } = useMemo(() => {
    const currency = subscription?.currency;
    const amount = items.reduce((sum, item) => sum + (item.total_amount || 0), 0) / 100;

    const formatter = currency
      ? new Intl.NumberFormat(undefined, {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : null;

    const cycle = subscription?.interval === "year" ? "Yearly" : "Monthly";

    let renewal = "-";
    if (subscription?.next_payment_date) {
      renewal = new Intl.DateTimeFormat(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      }).format(new Date(subscription.next_payment_date));
    }

    const cancelling = items.filter((item) => getEffectiveCancelsAt(item, subscription) != null).length;

    return {
      totalFormatted: formatter ? formatter.format(amount) : amount.toFixed(2),
      cycleLabel: cycle,
      renewalDate: renewal,
      pendingCancelCount: cancelling,
    };
  }, [items, subscription]);

  const stackedIcons = items.slice(0, MAX_STACK_ICONS);
  const overflowCount = Math.max(0, items.length - MAX_STACK_ICONS);

  return (
    <Card>
      <Left>
        <IconStack>
          {stackedIcons.map((item, index) => (
            <StackedIconWrap key={item.product_slug} style={{ zIndex: stackedIcons.length - index }}>
              {renderProductIcon(item.product_slug, CARD_COLOR)}
            </StackedIconWrap>
          ))}
          {overflowCount > 0 && (
            <StackedIconWrap muted style={{ zIndex: 0 }}>
              +{overflowCount}
            </StackedIconWrap>
          )}
        </IconStack>

        <TextGroup>
          <Title>{items.length} active plan{items.length === 1 ? "" : "s"}</Title>
          <Subtitle>
            {cycleLabel} billing · Renews {renewalDate}
            {pendingCancelCount > 0 && (
              <PendingNote> · {pendingCancelCount} ending soon</PendingNote>
            )}
          </Subtitle>
        </TextGroup>
      </Left>

      <Right>
        <TotalGroup>
          <TotalPrice>{totalFormatted}</TotalPrice>
          <TotalInterval>/{cycleLabel === "Yearly" ? "year" : "month"}</TotalInterval>
        </TotalGroup>

        {unsubscribedProducts.length > 0 && (
          <UpsellNote>+{unsubscribedProducts.length} more product{unsubscribedProducts.length === 1 ? "" : "s"} available</UpsellNote>
        )}

        <ViewAllButton onClick={onViewAll}>
          View all plans
          <ArrowRight size={16} />
        </ViewAllButton>
      </Right>
    </Card>
  );
};

export default PlansSummaryCard;

const Card = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  width: 100%;
  background: #ffffff;
  border-radius: 16px;
  box-shadow:
    0 -1px 2px rgba(10, 13, 18, 0.04),
    0 1px 2px rgba(10, 13, 18, 0.04),
    0 4px 12px rgba(10, 13, 18, 0.06);
  padding: 20px 24px;

  @media (max-width: 640px) {
    padding: 16px;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;

const IconStack = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const StackedIconWrap = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${(p) => (p.muted ? "#f2f4f7" : "#f4ebff")};
  color: #667085;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  margin-left: -10px;
  font-size: 12px;
  font-weight: 600;

  &:first-child {
    margin-left: 0;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Title = styled.span`
  color: #181d27;
  font-size: 16px;
  font-weight: 700;
`;

const Subtitle = styled.span`
  color: #667085;
  font-size: 13px;
  font-weight: 400;
`;

const PendingNote = styled.span`
  color: #c4320a;
  font-weight: 600;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-shrink: 0;
`;

const TotalGroup = styled.div`
  display: flex;
  align-items: baseline;
  gap: 3px;
`;

const TotalPrice = styled.span`
  color: #101828;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.02em;
`;

const TotalInterval = styled.span`
  color: #667085;
  font-size: 13px;
`;

const UpsellNote = styled.span`
  display: inline-flex;
  align-items: center;
  background: #f4ebff;
  color: #7f56d9;
  border-radius: 9999px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
`;

const ViewAllButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #f4ebff;
  color: #7f56d9;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;

  &:hover {
    background: #e9d7fe;
  }
`;
